import { roseStorage } from '@inboundx/shared/platform';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useCallback, useContext, useState } from 'react';
import { useBackofficeAuth } from '@/contexts/BackofficeAuthContext';
import { CLIENT_ENVIRONMENTS, type EnvironmentFilter, isOrigin } from '@/domain/filters';
import {
    DEFAULT_ENVIRONMENTS,
    parseStoredEnvironments,
    serializeEnvironments,
} from '@/lib/environment';
import { queryKeys } from '@/lib/queryKeys';

export type { EnvironmentFilter } from '@/domain/filters';

interface EnvironmentContextType {
    selectedEnvironments: EnvironmentFilter[];
    setSelectedEnvironments: (environments: EnvironmentFilter[]) => void;
    /**
     * Single-env helper for places that don't (yet) support multi-env, such as
     * analytics dashboards or deep-link URLs. Returns the only selected env if
     * exactly one is picked, otherwise falls back to 'production' (the default
     * for unscoped views).
     */
    primaryEnvironment: EnvironmentFilter;
}

const EnvironmentContext = createContext<EnvironmentContextType | undefined>(undefined);

/**
 * Restrict a selection to what a client may see: production, optionally widened
 * to their own Playground chats. Production is always kept — an empty selection
 * means "all environments", which for a non-staff user would expose test and
 * staging data for their domain.
 */
function clampToClientEnvironments(environments: EnvironmentFilter[]): EnvironmentFilter[] {
    const allowed = environments.filter((env) => CLIENT_ENVIRONMENTS.includes(env));
    return allowed.includes('production') ? allowed : [...DEFAULT_ENVIRONMENTS, ...allowed];
}

export function EnvironmentProvider({ children }: { children: ReactNode }) {
    const queryClient = useQueryClient();
    const { isStaff } = useBackofficeAuth();

    const [selectedEnvironments, setSelectedEnvironmentsState] = useState<EnvironmentFilter[]>(() =>
        parseStoredEnvironments(roseStorage.getBackofficeSelectedEnvironment())
    );

    const setSelectedEnvironments = useCallback(
        (environments: EnvironmentFilter[]) => {
            const next = isStaff ? environments : clampToClientEnvironments(environments);
            setSelectedEnvironmentsState(next);
            roseStorage.setBackofficeSelectedEnvironment(serializeEnvironments(next));

            // Invalidate list queries when environment changes
            // Stats are production-only so they don't need invalidation
            queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.visitors.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.accounts.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.topics.all });
        },
        [queryClient, isStaff]
    );

    // Clamp on read too, not just on write: the selection also arrives from the
    // `?environment=` URL param and from localStorage, so a client could otherwise
    // hand themselves `test` by editing either one.
    const effectiveEnvironments: EnvironmentFilter[] = isStaff
        ? selectedEnvironments
        : clampToClientEnvironments(selectedEnvironments);
    // Origins are not environments (IX-4845) — single-env views (analytics RPCs)
    // only understand real env values, and origin chats are stamped 'production'.
    const primary = effectiveEnvironments.length === 1 ? effectiveEnvironments[0] : 'production';
    const primaryEnvironment: EnvironmentFilter = isOrigin(primary) ? 'production' : primary;

    return (
        <EnvironmentContext.Provider
            value={{
                selectedEnvironments: effectiveEnvironments,
                setSelectedEnvironments,
                primaryEnvironment,
            }}
        >
            {children}
        </EnvironmentContext.Provider>
    );
}

export function useEnvironment() {
    const context = useContext(EnvironmentContext);
    if (context === undefined) {
        throw new Error('useEnvironment must be used within an EnvironmentProvider');
    }
    return context;
}
